import React, { useEffect, useMemo, useState } from "react";
import { highlightTokens, buildSearchHighlightTokens } from "./highlightTokens";
import { requestAskFlash, useAskContext } from "./askContextStore";
import { clearHistorySignal, publishHistorySignal } from "./historySignalStore";
import { PERIODS, concentration, distributionFromItems, distributionFromSummary, filterItems, formatHsk, learnedNames,
  mergeNames, normalizeName, periodFloor, queryTokens, tariffInfo, treeStatus } from "./historyFunnelModel";

/**
 * 관세사 신고이력 — 깔때기 패널(52차). 검색 결과 탭에서 검색어 모드로만 쓴다(호 모드는 StarHistoryPanel).
 *
 * 1층 검색어 다리: 한글 검색어 → LLM 영문 품명(51차 이벤트) · 이력에서 배운 이름 · 기간 · 수입/수출
 * 2층 세번별 신고 분포: 비율 막대 · 관세율표 품명 · 세율 · 표식(결정트리·현행 표) · 집중도 — 세번 클릭 = 지도 파란 길
 * 3층 선택 세번의 신고품명(중복 제거): 일치율(품명·표준품명·규격·성분 전체에 대고 잼) · 건수 · 최근 · 규격·성분 일치
 * 4층 개별 문서: 품명 클릭 때만 펼침(규격·성분·표준품명·기간). 거래처는 회사 가입 뒤 자사 모드에서만.
 *
 * 분포는 집계 API(`agency-history-summary`)가 있으면 그것(색인당 점수 상위 N건 표본 — 화면이 밝힌다), 없으면 표시된 문서(최대 50)로 만들고 그 사실을 밝힌다.
 * 58차: 서버가 원문 0건에 오타 교정·동의어·영문 철자로 다시 찾으면(`query_rewrite`) 그 사실을 같은 자리에 보이고 LLM 영문 품명 재검색은 하지 않는다. "그대로 보기"는 `rewrite=0`.
 * 숫자는 관행이지 판정이 아니다. 확정은 결정트리만 한다.
 */
const DIRECTION = { import: "수입", export: "수출" };
const ACCENT = "var(--audit-accent)";
const MUTED = "var(--audit-text-muted)";
const BORDER = "1px solid var(--audit-border)";
const TOP_ROWS = 5;
const TOP_NAMES = 30;

const number = (v) => (Number.isSafeInteger(v) && v >= 0 ? v.toLocaleString("ko-KR") : "—");
const linkBtn = { border: 0, background: "none", color: ACCENT, cursor: "pointer", fontFamily: "inherit", fontSize: 12, padding: 0 };

export default function HistoryFunnelPanel({ searchQuery }) {
  const q0 = String(searchQuery || "").trim();
  const [request, setRequest] = useState({ q: q0 });   // { q, raw?: true } — raw 는 서버 교정 없이 원문 그대로(rewrite=0)
  const [state, setState] = useState({ loading: true });
  const [summary, setSummary] = useState(null);
  const [period, setPeriod] = useState("12m");
  const [direction, setDirection] = useState("all");
  const [selected, setSelected] = useState(null);
  const [openName, setOpenName] = useState(null);
  const [details, setDetails] = useState({});
  // 51차: 한글 검색어가 0건이면 제안 신호가 알린 영문 신고 품명으로 한 번 다시 찾는다.
  const [altName, setAltName] = useState(null);
  const [fallback, setFallback] = useState(null);
  const ctx = useAskContext(q0);

  useEffect(() => {
    const on = (e) => { const d = e.detail || {}; if (d.query && d.name_en) setAltName({ query: String(d.query), name_en: String(d.name_en), name_ko: String(d.name_ko || "") }); };
    window.addEventListener("loginhs:proposal-loaded", on);
    return () => window.removeEventListener("loginhs:proposal-loaded", on);
  }, []);
  useEffect(() => { setFallback(null); setSelected(null); setOpenName(null); setRequest({ q: q0 }); }, [q0]);
  useEffect(() => {
    const controller = new AbortController();
    let current = true;
    setState({ loading: true });
    setSummary(null);
    const params = new URLSearchParams({ q: request.q, hs4: "" });
    if (request.raw) params.set("rewrite", "0");
    fetch(`/api/onto/agency-history-extra?${params}`, { signal: controller.signal })
      .then(async (r) => { if (!r.ok) throw new Error("검색 실패"); return r.json(); })
      .then((data) => {
        if (!current) return;
        setState({ data });
        // 55차: 탭 배지는 판단이 되는 숫자 = 이력이 가리키는 세번 수(받은 문서의 서로 다른 세번). 문서 수는 패널 안 각주로만.
        try {
          const received = Array.isArray(data?.items) ? data.items : [];
          const codes = new Set(received.map((it) => String(it?.hsk || "").replace(/\D/g, "")).filter((d) => d.length >= 4));
          const total = Number.isSafeInteger(data?.total) ? data.total : received.length;
          window.dispatchEvent(new CustomEvent("loginhs:history-count", { detail: { q: q0, count: codes.size, documents: total, lowerBound: data?.total_is_lower_bound === true } }));
        } catch (_) { /* ignore */ }
      })
      .catch((error) => { if (current && error.name !== "AbortError") setState({ error: true }); });
    return () => { current = false; controller.abort(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [request]);
  // 집계 API(제안 명세 broker_history_summary_v1) — 아직 없으면 조용히 문서 분포로 대신한다(fail-open).
  const from = periodFloor(period);
  useEffect(() => {
    if (!request.q) return undefined;
    const controller = new AbortController();
    let current = true;
    const params = new URLSearchParams({ q: request.q, direction: direction === "all" ? "" : direction, from: from || "" });
    if (request.raw) params.set("rewrite", "0");
    fetch(`/api/onto/agency-history-summary?${params}`, { signal: controller.signal })
      .then(async (r) => (r.ok ? r.json() : null))
      .then((data) => { if (current) setSummary(distributionFromSummary(data) ? data : null); })
      .catch(() => { if (current) setSummary(null); });
    return () => { current = false; controller.abort(); };
  }, [request, direction, from]);
  useEffect(() => {
    if (fallback || state.loading || state.error || !altName) return;
    if (state.data?.query_rewrite) return;   // 58차: 서버가 이미 교정해 찾았으면 LLM 영문 품명으로 다시 찾지 않는다
    if (altName.query !== q0 || request.q !== q0 || !/[가-힣]/.test(request.q)) return;
    if ((state.data?.items || []).length > 0) return;
    if (altName.name_en.trim().toUpperCase() === request.q.trim().toUpperCase()) return;
    setFallback({ from: request.q, to: altName.name_en });
    setRequest({ q: altName.name_en });
  }, [altName, state, request, q0, fallback]);

  const items = state.data?.items || [];
  const toks = useMemo(() => queryTokens(request.q), [request.q]);
  const marks = useMemo(() => buildSearchHighlightTokens(request.q), [request.q]);
  const filtered = useMemo(() => filterItems(items, { direction, from }), [items, direction, from]);
  const dist = useMemo(() => distributionFromSummary(summary) || distributionFromItems(filtered), [summary, filtered]);
  const conc = concentration(dist);
  const top = dist.rows[0]?.hsk || null;
  const current = selected && dist.rows.some((r) => r.hsk === selected) ? selected : top;
  const [showAll, setShowAll] = useState(false);
  const visibleRows = showAll ? dist.rows : dist.rows.slice(0, TOP_ROWS);
  const learned = useMemo(() => learnedNames(items, request.q), [items, request.q]);
  const namesSource = useMemo(() => {
    const fromSummary = dist.rows.find((r) => r.hsk === current)?.names;
    if (Array.isArray(fromSummary) && fromSummary.length) return fromSummary;
    return filtered.filter((it) => String(it.hsk || "").replace(/\D/g, "") === current);
  }, [dist, current, filtered]);
  const names = useMemo(() => mergeNames(namesSource, toks).slice(0, TOP_NAMES), [namesSource, toks]);
  // 57차: 지도에 이력 신호를 게시한다 — 세번별 비율·관측과 1위 세번의 "같은 물품" 근거(일치율 100% 관측). 판정이 아니라 근거다.
  useEffect(() => {
    if (!q0 || state.loading || state.error || !dist.rows.length) { if (q0) clearHistorySignal(q0); return undefined; }
    const byCode = Object.fromEntries(dist.rows.map((r) => [r.hsk, { share: r.share, observations: r.observations }]));
    const topRow = dist.rows[0];
    const topDocs = Array.isArray(topRow.names) && topRow.names.length ? topRow.names : filtered.filter((it) => String(it.hsk || "").replace(/\D/g, "") === topRow.hsk);
    const matched = mergeNames(topDocs, toks).filter((n) => n.rate === 100).reduce((s, n) => s + n.observations, 0);
    const secondRow = dist.rows[1] || null;
    publishHistorySignal({ q: q0, top: { hsk: topRow.hsk, share: topRow.share, observations: topRow.observations, matched },
      second: secondRow ? { hsk: secondRow.hsk, share: secondRow.share, observations: secondRow.observations } : null,
      byCode, documents: Number.isSafeInteger(state.data?.total) ? state.data.total : null, partial: dist.partial, sampled: dist.sampled === true, searched: request.q });
    return undefined;
  }, [q0, state, dist, filtered, toks, request.q]);
  useEffect(() => () => clearHistorySignal(q0), [q0]);

  // 관세율표 상세(세율·품명·현행 여부) — 분포 상위 호만, 호당 1회, fail-open.
  useEffect(() => {
    const hs4s = [...new Set(dist.rows.slice(0, TOP_ROWS).map((r) => r.hs4))].filter((h) => /^\d{4}$/.test(h) && !(h in details));
    if (!hs4s.length) return undefined;
    let current = true;
    hs4s.forEach((hs4) => {
      fetch(`/api/onto/heading-detail?hs=${hs4}&lang=kor`)
        .then(async (r) => (r.ok ? r.json() : null))
        .then((data) => { if (current) setDetails((prev) => (hs4 in prev ? prev : { ...prev, [hs4]: Array.isArray(data?.rows) ? data : null })); })
        .catch(() => { if (current) setDetails((prev) => (hs4 in prev ? prev : { ...prev, [hs4]: null })); });
    });
    return () => { current = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dist]);

  const pickHsk = (hsk) => { setSelected(hsk); setOpenName(null); requestAskFlash(q0, hsk); };
  const searchWith = (name) => { setFallback({ from: q0, to: name, manual: true }); setRequest({ q: name }); };
  const resetQuery = () => { setFallback({ from: q0, to: "", off: true }); setRequest({ q: q0 }); };
  // 58차: 서버 재검색 안내 — 응답 `query_rewrite{method, from, to}`. 그대로 보기는 같은 검색어를 rewrite=0 으로.
  const rewrite = !request.raw && state.data?.query_rewrite && state.data.query_rewrite.to ? state.data.query_rewrite : null;
  const REWRITE_METHOD = { typo_ko: "한글 오타 교정", synonym: "동의어", spelling: "이력 영문 철자 제안" };
  const resetRaw = () => { setFallback(null); setRequest({ q: q0, raw: true }); };
  const unfilteredEmpty = items.length === 0;
  const filteredEmpty = !unfilteredEmpty && filtered.length === 0 && !summary;
  const td = { padding: "8px 8px", borderTop: BORDER, verticalAlign: "top", overflowWrap: "anywhere" };
  const th = { padding: "5px 8px", textAlign: "left", fontWeight: 400, color: MUTED, fontSize: 11 };
  // 53차: 파랑은 선택된 세번 한 곳(글자·막대)에만. 나머지는 검정·회색 농도로 층을 낸다(칩·상자·배지 없음).
  const word = (active, onClick, label, pressed) => (
    <button key={label} type="button" aria-pressed={pressed} onClick={onClick}
      style={{ border: 0, background: "none", padding: "0 2px", cursor: "pointer", fontFamily: "inherit", fontSize: 12,
        color: active ? "var(--audit-text)" : MUTED, fontWeight: active ? 700 : 400 }}>{label}</button>
  );
  const sep = <span aria-hidden="true" style={{ color: "var(--audit-text-faint)" }}>·</span>;
  const nameWord = (label, tag, active, onClick, title) => (
    <span key={label} style={{ display: "inline-flex", alignItems: "baseline", gap: 4 }}>
      <button type="button" onClick={onClick} title={title}
        style={{ border: 0, background: "none", padding: "0 1px", cursor: "pointer", fontFamily: "inherit", fontSize: 12,
          color: active ? "var(--audit-text)" : "var(--audit-text-muted)", fontWeight: active ? 700 : 400,
          borderBottom: active ? "2px solid var(--audit-text)" : "2px solid transparent" }}>{label} <span style={{ fontSize: 10, fontWeight: 400, color: MUTED }}>{tag}</span></button>
    </span>
  );
  const stat = (label, value, key) => (
    <span key={key || label} style={{ display: "inline-flex", alignItems: "baseline", gap: 6 }}>
      <span style={{ fontSize: 18, fontWeight: 700, color: "var(--audit-text)" }}>{value}</span>{" "}
      <span style={{ color: MUTED }}>{label}</span>
    </span>
  );

  return <section aria-label="관세사 신고이력" style={{ marginBottom: 22, fontSize: 12 }}>
    <h3 style={{ fontSize: 14, margin: "0 0 4px" }}>관세사 신고이력 · 신고 분포</h3>
    <p style={{ color: MUTED, margin: "0 0 12px" }}>
      익명 이력 {state.data?.source_rows ? `원본 ${number(state.data.source_rows)}행` : ""} · 신고비율은 관행이며 법적 확정이 아닙니다 · 세번은 규격·성분을 보고 판단합니다 · 타사 이력은 분포·품명까지, 자사 상세(거래처·개별 신고)는 회사 가입 뒤 자사 모드에서 봅니다
    </p>

    {/* 1층 · 검색어 다리 — 현재 이름은 검은 밑줄, 후보 이름은 회색 글자 */}
    <div data-testid="history-bridge" style={{ display: "flex", flexWrap: "wrap", gap: "4px 10px", alignItems: "baseline", marginBottom: 4 }}>
      <span style={{ color: MUTED }}>검색어</span>
      <b>{q0 || "—"}</b>
      {(fallback && fallback.to) || altName?.query === q0 || learned.length > 0 ? <span aria-hidden="true" style={{ color: MUTED }}>→</span> : null}
      {altName && altName.query === q0 && altName.name_en && nameWord(altName.name_en, "LLM", request.q === altName.name_en, () => searchWith(altName.name_en), "LLM이 읽은 영문 신고 품명 · 이름일 뿐 세번이 아닙니다")}
      {learned.filter((n) => n !== (altName?.name_en || "").toUpperCase() && n !== request.q.toUpperCase()).map((n) => nameWord(n, "이력", false, () => searchWith(n), "이력에서 함께 나온 이름"))}
      <span style={{ marginLeft: "auto", display: "flex", gap: 14, flexWrap: "wrap" }}>
        <span style={{ display: "inline-flex", gap: 5, alignItems: "baseline" }}>{PERIODS.flatMap((p, i) => [i > 0 && <span key={`s${p.key}`}>{sep}</span>, word(period === p.key, () => setPeriod(p.key), p.label, period === p.key)])}</span>
        <span style={{ display: "inline-flex", gap: 5, alignItems: "baseline" }}>{[["all", "전체"], ["import", "수입"], ["export", "수출"]].flatMap(([k, label], i) => [i > 0 && <span key={`s${k}`}>{sep}</span>, word(direction === k, () => setDirection(k), label, direction === k)])}</span>
      </span>
    </div>
    {rewrite && (
      <div data-testid="history-query-rewrite" style={{ color: MUTED, marginBottom: 12 }}>
        <b style={{ color: "var(--audit-text)" }}>{rewrite.from || request.q}</b>은(는) 신고이력에 0건이라 {REWRITE_METHOD[rewrite.method] || "교정"}으로 <b style={{ color: "var(--audit-text)" }}>{rewrite.to}</b>(으)로 다시 찾았습니다 · 이름은 교정일 뿐 세번이 아닙니다 ·{" "}
        <button type="button" style={{ ...linkBtn, color: MUTED, textDecoration: "underline" }} onClick={resetRaw}>처음 검색어 그대로 보기</button>
      </div>
    )}
    {!rewrite && fallback && fallback.to && !fallback.off && (
      <div data-testid="history-english-fallback" style={{ color: MUTED, marginBottom: 12 }}>
        {fallback.manual ? <>{fallback.to}(으)로 찾는 중 · </> : <><b style={{ color: "var(--audit-text)" }}>{fallback.from}</b>은(는) 신고이력에 0건이라 LLM이 읽은 영문 신고 품명 <b style={{ color: "var(--audit-text)" }}>{fallback.to}</b>(으)로 다시 찾았습니다 · 이름은 제안일 뿐 세번이 아닙니다 · </>}
        <button type="button" style={{ ...linkBtn, color: MUTED, textDecoration: "underline" }} onClick={resetQuery}>처음 검색어 그대로 보기</button>
      </div>
    )}

    {state.loading ? <p role="status">관세사 이력 검색 중…</p>
      : state.error ? <p role="alert">이력 검색에 연결하지 못했습니다. 다시 검색해 주세요.</p>
      : unfilteredEmpty ? <p>이 검색 대상에서 검색된 후보가 없습니다. 실제 신고이력 부재를 뜻하지 않습니다.{/[가-힣]/.test(request.q) && " 이력 품명은 대부분 영문이라 영문 품명으로 다시 검색해 보세요."}</p>
      : <>
        {/* 요약 — 상자 없이 큰 숫자 한 줄 */}
        <div data-testid="history-summary" style={{ display: "flex", flexWrap: "wrap", gap: "6px 26px", margin: "10px 0 16px" }}>
          {stat(dist.partial ? "신고 관측 · 표시분" : dist.sampled ? "신고 관측 · 표본" : "신고 관측", number(dist.total))}
          {stat(conc.kind === "single" ? "한 세번 집중" : conc.kind === "split" ? "둘로 갈림" : conc.kind === "spread" ? "흩어짐 · 최다" : "분포 없음",
            conc.kind === "single" ? `${Math.round(conc.topShare)}%` : conc.kind === "split" ? conc.label.replace("둘로 갈림 ", "") : conc.kind === "spread" ? `${Math.round(conc.topShare)}%` : "—", "conc")}
          {stat("세번", dist.rows.length)}
          {ctx && current && (treeStatus(current, ctx) === "live"
            ? <span style={{ display: "inline-flex", alignItems: "baseline", gap: 6 }}><span style={{ color: MUTED }}>결정트리</span>{" "}<span style={{ fontSize: 18, fontWeight: 700 }}>{formatHsk(current)}</span>{" "}<span style={{ color: MUTED }}>살아 있음</span></span>
            : <span style={{ display: "inline-flex", alignItems: "baseline", gap: 6 }}><span style={{ color: MUTED }}>결정트리</span>{" "}<span style={{ fontSize: 18, fontWeight: 700 }}>{formatHsk(current)}</span>{" "}<span style={{ color: MUTED }}>트리 밖</span></span>)}
        </div>
        {filteredEmpty ? <p>{PERIODS.find((p) => p.key === period)?.label}·{direction === "all" ? "전체" : DIRECTION[direction]} 조건에는 표시된 문서가 없습니다. <button type="button" style={linkBtn} onClick={() => { setPeriod("all"); setDirection("all"); }}>전체 기간·전체 방향으로 보기</button></p> : null}

        {/* 2층 · 세번별 신고 분포 — 파랑은 선택 행의 세번·막대에만, 선택 행은 왼쪽 검은 선 */}
        {dist.rows.length > 0 && <div style={{ marginBottom: 18 }}>
          <div style={{ marginBottom: 2 }}>
            <b>세번별 신고 분포</b>
            <span style={{ color: MUTED, marginLeft: 8 }}>{dist.partial
              ? `이력 문서 ${number(state.data?.total)}${state.data?.total_is_lower_bound ? "+" : ""}개(전체 기간) 중 상위 ${items.length}개 수신 · 그중 ${filtered.length}개 표시 기준 · 집계 API 연결 전이라 전체 분포가 아닙니다`
              : dist.sampled
                ? `검색 점수 상위 ${number(dist.sampleDocuments)}건${dist.samplePerIndex ? `(색인당 ${number(dist.samplePerIndex)}건)` : ""} 표본의 분포 · 전체 일치 문서 ${number(dist.totalDocuments)}${dist.totalLowerBound ? "+" : ""}개 · 표본이라 전체 분포와 다를 수 있습니다`
                : "전체 이력 집계"} · 세번을 누르면 지도에 그 세번의 길이 켜집니다</span>
          </div>
          <div style={{ overflowX: "auto" }}>
            <table data-testid="history-distribution" style={{ width: "100%", borderCollapse: "collapse", minWidth: 640 }}>
              <thead><tr>{["세번", "신고비율", "관측", "관세율표 품명", "세율", "최근", "표식"].map((h, i) => <th key={h} style={{ ...th, paddingLeft: i === 0 ? 10 : 8 }}>{h}</th>)}</tr></thead>
              <tbody>{visibleRows.map((r) => {
                const info = tariffInfo(r.hsk, details[r.hs4]);
                const detailKnown = details[r.hs4] !== undefined && details[r.hs4] !== null;
                const tree = treeStatus(r.hsk, ctx);
                const active = r.hsk === current;
                const tone = active ? "var(--audit-text)" : "var(--audit-text-muted)";
                return <tr key={r.hsk} data-testid="history-hs-row" data-active={active ? "1" : "0"} style={{ color: tone, boxShadow: active ? "inset 3px 0 0 var(--audit-text)" : "none" }}>
                  <td style={{ ...td, paddingLeft: 10 }}><button type="button" onClick={() => pickHsk(r.hsk)} style={{ ...linkBtn, fontFamily: "ui-monospace, monospace", color: active ? ACCENT : tone, fontWeight: active ? 700 : 400 }}>{formatHsk(r.hsk)}</button></td>
                  <td style={{ ...td, minWidth: 160 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <div style={{ flex: 1, height: 6, background: "var(--audit-bg-subtle)", borderRadius: 3 }}><div style={{ width: `${Math.max(1, r.share)}%`, height: 6, background: active ? ACCENT : "var(--audit-text-faint)", borderRadius: 3 }} /></div>
                      <span style={{ width: 48, textAlign: "right", fontWeight: active ? 700 : 400 }}>{r.share}%</span>
                    </div>
                  </td>
                  <td style={td}>{number(r.observations)}</td>
                  <td style={td}>{info.name || (detailKnown ? <span style={{ color: MUTED }}>현행 관세율표에 없음</span> : "—")}</td>
                  <td style={td}>{info.basic != null ? <>{info.basic}%{info.ftaMin != null && <span style={{ color: MUTED }}> FTA {info.ftaMin}%</span>}</> : "—"}</td>
                  <td style={td}>{r.last || "—"}</td>
                  <td style={td}>
                    {tree === "live" && <span>● 트리 살아 있음</span>}
                    {tree === "outside" && <span style={{ color: MUTED }}>○ 트리 밖</span>}
                    {detailKnown && !info.found && <span style={{ color: MUTED, marginLeft: tree ? 6 : 0 }}>만료 가능</span>}
                  </td>
                </tr>;
              })}</tbody>
            </table>
          </div>
          {dist.rows.length > TOP_ROWS && <div style={{ padding: "5px 10px", borderTop: BORDER }}>
            <button type="button" style={{ ...linkBtn, color: MUTED, textDecoration: "underline" }} onClick={() => setShowAll((v) => !v)}>{showAll ? "상위 5개만 보기" : `그 밖 ${dist.rows.length - TOP_ROWS}개 세번 펼치기`}</button>
          </div>}
        </div>}

        {/* 3층 · 선택 세번의 신고품명 — 일치율은 숫자 굵기로만, 낮은 행은 통째로 옅게 */}
        {current && <div>
          <div style={{ marginBottom: 2 }}>
            <b>{formatHsk(current)} 신고품명</b>
            <span style={{ color: MUTED, marginLeft: 8 }}>중복 제거 {names.length}개 · 일치율 순 · 규격·성분 일치 먼저 · 품명을 누르면 규격·성분·기간이 펼쳐집니다</span>
          </div>
          <div style={{ overflowX: "auto" }}>
            <table data-testid="history-names" style={{ width: "100%", borderCollapse: "collapse", minWidth: 560 }}>
              <thead><tr>{["일치율", "거래품명 · 규격·성분", "구분", "관측", "최근"].map((h, i) => <th key={h} style={{ ...th, paddingLeft: i === 0 ? 10 : 8 }}>{h}</th>)}</tr></thead>
              <tbody>{names.map((n) => {
                const open = openName === n.key;
                const tone = n.rate == null || n.rate >= 60 ? "var(--audit-text)" : "var(--audit-text-muted)";
                return <React.Fragment key={n.key}>
                  <tr data-testid="history-name-row" style={{ color: tone }}>
                    <td style={{ ...td, paddingLeft: 10 }}>{n.rate == null ? "—"
                      : <span data-testid="history-match-rate" style={{ fontWeight: n.rate >= 90 ? 700 : 400, color: n.rate >= 90 ? "var(--audit-text)" : tone }}>{n.rate}%</span>}</td>
                    <td style={td}>
                      <button type="button" style={{ ...linkBtn, color: "inherit", textAlign: "left" }} onClick={() => setOpenName(open ? null : n.key)}>{highlightTokens(n.name, marks)}</button>
                      {n.standard_name && normalizeName(n.standard_name) !== n.key && <span style={{ marginLeft: 6, fontSize: 11, color: MUTED }}>표준 {n.standard_name}</span>}
                      {n.specHit && <span title="검색어가 규격·성분에서도 맞았습니다" style={{ marginLeft: 6, fontSize: 11, color: MUTED }}>규격·성분 ✓</span>}
                      {/* 54차: 거래품명은 담당자가 세관이 알 수 있는 말로 정리한 줄, 규격·성분은 인보이스 원문 — 둘 다 보인다(규격은 둘째 줄, 전체는 클릭). */}
                      {(n.spec || n.composition) && <div data-testid="history-name-spec" style={{ fontSize: 11, color: MUTED, marginTop: 2, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", maxWidth: 520 }}>
                        {n.spec ? highlightTokens(n.spec, marks) : ""}{n.spec && n.composition ? " · " : ""}{n.composition ? highlightTokens(n.composition, marks) : ""}
                      </div>}
                    </td>
                    <td style={td}>{n.directions.map((d) => DIRECTION[d] || d).join("·") || "—"}</td>
                    <td style={td}>{number(n.observations)}</td>
                    <td style={td}>{n.last || "—"}</td>
                  </tr>
                  {open && <tr data-testid="history-name-detail"><td colSpan={5} style={{ ...td, paddingLeft: 10, background: "var(--audit-bg-subtle)", color: "var(--audit-text)" }}>
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: "4px 16px" }}>
                      <span><span style={{ color: MUTED }}>모델·규격</span> {n.spec ? highlightTokens(n.spec, marks) : "—"}</span>
                      <span><span style={{ color: MUTED }}>성분</span> {n.composition ? highlightTokens(n.composition, marks) : "—"}</span>
                      <span><span style={{ color: MUTED }}>표준품명</span> {n.standard_name || "—"}</span>
                      <span><span style={{ color: MUTED }}>기간</span> {n.first || "—"} ~ {n.last || "—"}</span>
                      <span><span style={{ color: MUTED }}>문서</span> {n.entries.length}개 · 관측 {number(n.observations)} (독립 신고 수 아님)</span>
                      <span style={{ color: MUTED }}>거래처·신고번호는 자사 모드(회사 가입 뒤)에서만 보입니다</span>
                    </div>
                  </td></tr>}
                </React.Fragment>;
              })}</tbody>
            </table>
          </div>
        </div>}
      </>}
  </section>;
}
